// The single RTK Query service for all of M3 Risk Management — the register table's list/KPI
// reads (built independently, on another branch) and the "Log New Risk" flow's taxonomy/create/
// attachment endpoints (built here) both live in this one file; they used to be two separate
// files (`riskApis.ts` and `risksApi.ts`) that landed side by side in the same merge, which is
// exactly the kind of accidental duplication that's easy to miss and confusing to maintain.
// `riskApis.ts` was kept as the canonical name/casing.
//
// All endpoints below are real and confirmed against apps/backend/src/feature-modules/risk/
// risk.type.ts / risk.route.ts. `createRisk` is the combined create+score call (permission
// CREATE_RISK) — logging and scoring are one atomic call, so a risk is never persisted unscored.
// It returns the flat `RiskDetail` row itself (score fields denormalized onto it), not a
// `{risk, score}` wrapper.

import { baseApi } from '@/store/baseApi';
import type { ApiResponse } from '../../../services/services.types';
import { findingChanged } from '@/features/AuditManagement/services/auditTags';
import type {
  DecideFindingRiskRequest,
  DecideFindingRiskResponse,
  GetIdentifiedRisksParams,
  IdentifiedRiskDetail,
  IdentifiedRiskListResponse,
} from './types/identifiedRisk.types';
import type {
  CreateRiskPayload,
  IApproveClosureRequest,
  IApproveTreatmentPlanRequest,
  IApproveTreatmentPlanResponse,
  ICloseRiskRequest,
  ICloseRiskResponse,
  ICreateTreatmentPlanRequest,
  IGetRiskAuditLogRequest,
  IGetRiskAuditLogResponse,
  IGetRiskScoreRequest,
  IGetRiskScoreResponse,
  IGetTreatmentActionRequest,
  IGetTreatmentActionResponse,
  IRecordRiskScoreRequest,
  IRecordRiskScoreResponse,
  ISendActionForReviewRequest,
  ISendActionForReviewResponse,
  ISubmitTreatmentActionRequest,
  ISubmitTreatmentActionResponse,
  IVerifyTreatmentActionRequest,
  IVerifyTreatmentActionResponse,
  RiskDetail,
  TaxonomyDepartment,
} from '../types/risk.types';
import type {
  DecideRiskClosureRequest,
  DecideRiskClosureResponse,
  GetRiskListParams,
  GetReviewCycleParams,
  GetTreatmentStatusReportParams,
  IEditRiskRequest,
  IGetRiskByIdRequest,
  IGetRiskByIdResponse,
  IGetRiskKpisResponse,
  IGetRiskReviewDetailRequest,
  IGetRiskTreatmentRequest,
  IGetRiskTreatmentResponse,
  RemoveRiskAttachmentRequest,
  ReviewCycleCisoQueueResponse,
  ReviewCycleKpis,
  ReviewCycleWorklistResponse,
  RiskApiTreatmentPlanDetail,
  RiskListResponse,
  RiskReviewDetailResponse,
  SubmitRiskReviewRequest,
  SubmitRiskReviewResponse,
  TreatmentKpisResponse,
  TreatmentStatusReportResponse,
} from './types/risk.types';

type GetRiskTaxonomyResponse = {
  departments: TaxonomyDepartment[];
};

type GetRiskAttachmentUploadUrlRequest = {
  fileName: string;
  mimeType: string;
  size: number;
};

type GetRiskAttachmentUploadUrlResponse = {
  uploadUrl: string;
  tempKey: string;
  expiresAt: string;
};

const riskApis = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // The register table's list + KPI reads. Both tag 'Risks' — createRisk below invalidates it,
    // so a freshly created risk shows up here automatically.
    getRiskList: builder.query<RiskListResponse, GetRiskListParams | void>({
      query: ({
        page = 1,
        limit = 10,
        sortBy,
        sortOrder,
        searchText,
        department,
        status,
        source,
        riskLevel,
        exceedsAppetite,
        identifiedFrom,
        identifiedTo,
        processIds,
        subProcessIds,
        ownerIds,
      } = {}) => ({
        url: '/m3/risks',
        method: 'GET',
        params: {
          page,
          limit,
          ...(sortBy && sortOrder && { sortBy, sortOrder }),
          ...(searchText && { searchText }),
          ...(department && { department }),
          ...(status && { status }),
          ...(source && { source }),
          ...(riskLevel && { riskLevel }),
          ...(exceedsAppetite !== undefined && { exceedsAppetite }),
          ...(identifiedFrom && { identifiedFrom }),
          ...(identifiedTo && { identifiedTo }),
          ...(processIds?.length && { processIds }),
          ...(subProcessIds?.length && { subProcessIds }),
          ...(ownerIds?.length && { ownerIds }),
        },
      }),
      transformResponse: (response: ApiResponse<RiskListResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    getRiskKpis: builder.query<IGetRiskKpisResponse, void>({
      query: () => ({ url: '/m3/risks/kpis', method: 'GET' }),
      transformResponse: (response: ApiResponse<IGetRiskKpisResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    // Mitigation Plans tab's table. riskLevel/sortBy/sortOrder are applied server-side, before
    // pagination, across the whole report — safe to page through.
    getTreatmentStatusReport: builder.query<
      TreatmentStatusReportResponse,
      GetTreatmentStatusReportParams | void
    >({
      query: ({ page = 1, limit = 10, riskLevel, sortBy, sortOrder } = {}) => ({
        url: '/m3/risks/treatment-status-report',
        method: 'GET',
        params: {
          page,
          limit,
          ...(riskLevel && { riskLevel }),
          ...(sortBy && sortOrder && { sortBy, sortOrder }),
        },
      }),
      transformResponse: (response: ApiResponse<TreatmentStatusReportResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    // Mitigation Plans tab's 5 stat cards. Counts every matching plan, not just the current
    // page, so the tiles hold still while the table below is filtered — same as getRiskKpis.
    getTreatmentKpis: builder.query<TreatmentKpisResponse, void>({
      query: () => ({ url: '/m3/risks/treatment-kpis', method: 'GET' }),
      transformResponse: (response: ApiResponse<TreatmentKpisResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    // Tagged separately from 'Risks' — taxonomy (departments/processes/sub-processes) has no
    // relationship to whether a risk was created, so it must not refetch off `createRisk`'s
    // invalidation. It was wrongly sharing the 'Risks' tag before, causing an extra taxonomy
    // fetch after every risk creation.
    getRiskTaxonomy: builder.query<GetRiskTaxonomyResponse, void>({
      query: () => ({ url: '/m3/risks/taxonomy' }),
      transformResponse: (response: ApiResponse<GetRiskTaxonomyResponse>) => response.data,
      providesTags: ['RiskTaxonomy'],
    }),
    createRisk: builder.mutation<RiskDetail, CreateRiskPayload>({
      query: (body) => ({ url: '/m3/risks', method: 'POST', data: body }),
      transformResponse: (response: ApiResponse<RiskDetail>) => response.data,
      // Logging a risk from a draft deletes the draft server-side, and the finding's detail then
      // reports no identifiedRisk (shown as "Logged"), so only that case also refreshes the drafts
      // and the audit data — an ordinary risk create must not refetch every cached audit query.
      invalidatesTags: (_result, _error, arg) =>
        arg.identifiedRiskId ? ['Risks', 'IdentifiedRisks', 'Audits'] : ['Risks'],
    }),
    // Systematic Risk Conversion: the Compliance Manager decides whether a Major/Critical finding
    // with a root cause is systemic. CONVERTED creates a draft (see getIdentifiedRisks);
    // NOT_SYSTEMIC records the justification on the finding only.
    decideFindingRisk: builder.mutation<DecideFindingRiskResponse, DecideFindingRiskRequest>({
      query: ({ findingId, auditId: _auditId, ...body }) => ({
        url: `/m3/identified-risks/findings/${findingId}/decision`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<DecideFindingRiskResponse>) => response.data,
      invalidatesTags: (_result, _error, { auditId }) => [
        'IdentifiedRisks',
        ...findingChanged(auditId),
      ],
    }),
    // The register's "Drafts" segment.
    getIdentifiedRisks: builder.query<IdentifiedRiskListResponse, GetIdentifiedRisksParams | void>({
      query: ({
        page = 1,
        limit = 10,
        searchText,
        sortBy,
        sortOrder,
        auditId,
        controlId,
      } = {}) => ({
        url: '/m3/identified-risks',
        method: 'GET',
        params: {
          page,
          limit,
          ...(searchText && { searchText }),
          ...(sortBy && sortOrder && { sortBy, sortOrder }),
          ...(auditId && { auditId }),
          ...(controlId && { controlId }),
        },
      }),
      transformResponse: (response: ApiResponse<IdentifiedRiskListResponse>) => response.data,
      providesTags: ['IdentifiedRisks'],
    }),
    // One draft, to prefill the Log Risk form.
    getIdentifiedRisk: builder.query<IdentifiedRiskDetail, string>({
      query: (id) => ({ url: `/m3/identified-risks/${id}`, method: 'GET' }),
      transformResponse: (response: ApiResponse<IdentifiedRiskDetail>) => response.data,
      providesTags: ['IdentifiedRisks'],
    }),
    // Step one of the two-step upload (permission CREATE_RISK_ATTACHMENT): PUT the file to
    // `uploadUrl`, then pass `tempKey` in the `attachments` array of `createRisk`.
    // Uncommitted temp objects are also reclaimed by the backend's orphan-cleanup sweep, but
    // LogRiskDetailsStep.tsx removes an in-progress attachment eagerly via
    // `removeRiskAttachment` below when the user removes it before final submit.
    getRiskAttachmentUploadUrl: builder.mutation<
      GetRiskAttachmentUploadUrlResponse,
      GetRiskAttachmentUploadUrlRequest
    >({
      query: (body) => ({ url: '/m3/risks/attachments/upload-url', method: 'POST', data: body }),
      transformResponse: (response: ApiResponse<GetRiskAttachmentUploadUrlResponse>) =>
        response.data,
    }),
    // Step two's undo: deletes an uncommitted temp attachment from S3 (permission
    // CREATE_RISK_ATTACHMENT, same as upload — a real risk-scoped endpoint, unlike the earlier
    // stopgap of reusing EvidenceManagement's DELETE /m6/evidence/file). Also doubles as the
    // Overview edit form's "remove an already-committed attachment" call — same endpoint, the
    // `attachmentId` + `riskId` branch instead of `objectKey` (see RemoveRiskAttachmentRequest).
    removeRiskAttachment: builder.mutation<{ success: boolean }, RemoveRiskAttachmentRequest>({
      query: (params) => ({
        url: '/m3/risks/attachments/file',
        method: 'DELETE',
        params,
      }),
      transformResponse: (response: ApiResponse<{ success: boolean }>) => response.data,
      // Only the committed-attachment branch (`attachmentId` + `riskId`, used by the Overview
      // edit form) should bust the 'Risks' cache — that's what keeps getRiskById's stale
      // attachments list from reappearing after Cancel. The temp-upload undo branch
      // (`objectKey`, used by LogRiskDetailsStep pre-submit) has nothing yet in the risk cache
      // to invalidate, so it's left alone to avoid needless list refetches.
      invalidatesTags: (_result, _error, arg) => ('attachmentId' in arg ? ['Risks'] : []),
    }),
    getRiskById: builder.query<IGetRiskByIdResponse, IGetRiskByIdRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IGetRiskByIdResponse }) => response.data,
      providesTags: ['Risks'],
    }),
    getRiskScore: builder.query<IGetRiskScoreResponse, IGetRiskScoreRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}/scores`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IGetRiskScoreResponse }) => response.data,
      providesTags: ['Risks'],
    }),
    // Real route is POST /:riskId/re-score (confirmed against risk.route.ts) — GET
    // /:riskId/scores is score *history* only; there is no POST at that path.
    recordRiskScore: builder.mutation<IRecordRiskScoreResponse, IRecordRiskScoreRequest>({
      query: ({ riskId, ...body }) => ({
        url: `/m3/risks/${riskId}/re-score`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<IRecordRiskScoreResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    editRiskById: builder.mutation<void, IEditRiskRequest>({
      query: ({ riskId, ...rest }) => ({
        url: `/m3/risks/${riskId}`,
        method: 'PATCH',
        data: rest,
      }),
      invalidatesTags: ['Risks'],
    }),
    getRiskAuditLog: builder.query<IGetRiskAuditLogResponse, IGetRiskAuditLogRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}/auditLog`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IGetRiskAuditLogResponse }) => response.data,
      providesTags: ['Risks'],
    }),
    // For the header's Approve/Reject flow "is a plan pending?" check (active?.status ===
    // 'PENDING_APPROVAL') as well as the Treatment tab's own content.
    getRiskTreatment: builder.query<IGetRiskTreatmentResponse, IGetRiskTreatmentRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}/treatment`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IGetRiskTreatmentResponse }) => response.data,
      providesTags: ['Risks'],
    }),
    createTreatmentPlan: builder.mutation<RiskApiTreatmentPlanDetail, ICreateTreatmentPlanRequest>({
      query: ({ riskId, ...body }) => ({
        url: `/m3/risks/${riskId}/treatment`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<RiskApiTreatmentPlanDetail>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // Review Cycle tab's worklist — the response shape genuinely differs by caller role (R2/R4
    // vs R3/CISO), so this is typed as a union rather than one fixed shape; the caller narrows
    // using its own already-known role, the same way the backend itself branches (see
    // review-cycle.md spec §1.1). `outcome` is R2-only, silently ignored for R3/R4. `searchText`/
    // `sortBy`/`sortOrder` are real, full-dataset, server-side now (PR #464) — applied uniformly
    // to both role branches, not just R2/R4.
    getReviewCycle: builder.query<
      ReviewCycleWorklistResponse | ReviewCycleCisoQueueResponse,
      GetReviewCycleParams | void
    >({
      query: ({ page = 1, limit = 20, outcome, searchText, sortBy, sortOrder } = {}) => ({
        url: '/m3/risks/review-cycle',
        method: 'GET',
        params: {
          page,
          limit,
          ...(outcome && { outcome }),
          ...(searchText && { searchText }),
          ...(sortBy && { sortBy, sortOrder: sortOrder ?? 'ASC' }),
        },
      }),
      transformResponse: (
        response: ApiResponse<ReviewCycleWorklistResponse | ReviewCycleCisoQueueResponse>
      ) => response.data,
      providesTags: ['Risks'],
    }),
    // The 4 stat cards above the Review Cycle worklist — R2/R4 only, the backend 403s a CISO
    // caller (`REVIEW_CYCLE_KPIS_NOT_AVAILABLE_FOR_CISO`) even though they hold
    // READ_RISK_REVIEW_CYCLE too, so this is only ever called from ReviewCycle.tsx's non-CISO
    // branch — never gate this call on the permission alone (see ReviewCycle.tsx).
    getReviewCycleKpis: builder.query<ReviewCycleKpis, void>({
      query: () => ({ url: '/m3/risks/review-cycle-kpis', method: 'GET' }),
      transformResponse: (response: ApiResponse<ReviewCycleKpis>) => response.data,
      providesTags: ['Risks'],
    }),
    // The Risk Owner's (R4-only — CREATE_RISK_REVIEW_RESPONSE was narrowed off R2) response to
    // an open Annual Assessment. `outcome` in the response tells the caller what actually
    // happened server-side (immediate closure vs. routed to CISO vs. confirmed/flagged) — the
    // frontend must not predict this from `everRatedHigh` or anything else client-side.
    submitRiskReview: builder.mutation<SubmitRiskReviewResponse, SubmitRiskReviewRequest>({
      query: ({ riskId, ...body }) => ({
        url: `/m3/risks/${riskId}/review`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<SubmitRiskReviewResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // The Review & History tab — a risk's currently OPEN assessment (if any) plus every past
    // cycle, newest first. Not role-scoped beyond the same R4 visibility mask
    // `submitRiskReview` already applies; a never-reviewed risk returns empty arrays, not a 404.
    getRiskReviewDetail: builder.query<RiskReviewDetailResponse, IGetRiskReviewDetailRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}/review`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<RiskReviewDetailResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    // The CISO's decision on a review-driven closure (design reference `#approve-closure-modal`,
    // ReviewCycleCisoQueue's row action) — reached only for a review that was routed to them
    // (an ever-High risk answered "no longer relevant").
    decideRiskClosure: builder.mutation<DecideRiskClosureResponse, DecideRiskClosureRequest>({
      query: ({ riskId, approvalId, ...body }) => ({
        url: `/m3/risks/${riskId}/close/${approvalId}/approve`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<DecideRiskClosureResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // R3's decision on a pending plan (permission APPROVE_RISK_TREATMENT_PLAN) — one endpoint for
    // both Approve and Reject, distinguished by `decision` in the body.
    approveTreatmentPlan: builder.mutation<
      IApproveTreatmentPlanResponse,
      IApproveTreatmentPlanRequest
    >({
      query: ({ riskId, planId, ...body }) => ({
        url: `/m3/risks/${riskId}/treatment/${planId}/approve`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<IApproveTreatmentPlanResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // The action owner's (R4) completion submission for one action item (permission
    // SUBMIT_RISK_TREATMENT_ACTION) — stages evidence, moving OPEN to IN_PROGRESS. It no longer
    // hands the action to the CM by itself; repeat this call to add more files/notes, then
    // sendActionForReview (below) to actually submit for review. Evidence is a temp upload from
    // the same getRiskAttachmentUploadUrl presign as risk attachments.
    submitTreatmentAction: builder.mutation<
      ISubmitTreatmentActionResponse,
      ISubmitTreatmentActionRequest
    >({
      query: ({ riskId, planId, taskId, ...body }) => ({
        url: `/m3/risks/${riskId}/treatment/${planId}/actions/${taskId}/submit`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<ISubmitTreatmentActionResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // The action owner's (R4) actual hand-off to the CM for one action item (permission
    // SUBMIT_RISK_TREATMENT_ACTION) — moves that task to PENDING_REVIEW. The backend rejects the
    // call if this action still has no evidence (when required) or if any sibling action on the
    // same plan is still OPEN (ACTIONS_NOT_ALL_STARTED) — Treatment.tsx's "Send for Review" button
    // gates on the same condition and calls this once per action.
    sendActionForReview: builder.mutation<
      ISendActionForReviewResponse,
      ISendActionForReviewRequest
    >({
      query: ({ riskId, planId, taskId }) => ({
        url: `/m3/risks/${riskId}/treatment/${planId}/actions/${taskId}/send-for-review`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<ISendActionForReviewResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // One action item's live state (permission READ_RISK_REGISTER, readable by its own owner and
    // by R2/R3) — unlike the plan-wide getRiskTreatment read, this includes the action's evidence
    // attachments with presigned view URLs and `canSendForReview`. The risk's general
    // `attachments` list (getRiskById) excludes action evidence entirely (it has this dedicated
    // read instead), so Treatment.tsx calls this once per action row to render what's been
    // uploaded so far.
    getTreatmentAction: builder.query<IGetTreatmentActionResponse, IGetTreatmentActionRequest>({
      query: ({ riskId, planId, taskId }) => ({
        url: `/m3/risks/${riskId}/treatment/${planId}/actions/${taskId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<IGetTreatmentActionResponse>) => response.data,
      providesTags: ['Risks'],
    }),
    // The CM's (R2) decision on a submitted action item (permission
    // UPDATE_RISK_TREATMENT_VERIFICATION) — one endpoint for both Verify and Reject, distinguished
    // by `decision` in the body, same pattern as approveTreatmentPlan above. VERIFY moves the task
    // to DONE; REJECT returns it to IN_PROGRESS and clears its submitted evidence.
    verifyTreatmentAction: builder.mutation<
      IVerifyTreatmentActionResponse,
      IVerifyTreatmentActionRequest
    >({
      query: ({ riskId, planId, taskId, ...body }) => ({
        url: `/m3/risks/${riskId}/treatment/${planId}/actions/${taskId}/verify`,
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<IVerifyTreatmentActionResponse>) => response.data,
      invalidatesTags: ['Risks'],
    }),
    // R2's closure request (permission CREATE_RISK_CLOSURE) — Accept-reason only, for now (see
    // ICloseRiskRequest). `status` in the response tells the caller which of the two outcomes
    // happened: an ever-High risk stops at PENDING_CISO_APPROVAL instead of closing outright.
    closeRisk: builder.mutation<ICloseRiskResponse, ICloseRiskRequest>({
      query: ({ riskId, ...body }) => ({
        url: `/m3/risks/${riskId}/close`,
        method: 'POST',
        data: body,
      }),
      // `message` comes from the wrapper, not `.data` — it's what tells CLOSED apart from
      // PENDING_CISO_APPROVAL in the caller's own words ("Risk closed and archived" vs. "Closure
      // submitted for CISO approval").
      transformResponse: (response: ApiResponse<Omit<ICloseRiskResponse, 'message'>>) => ({
        ...response.data,
        message: response.message,
      }),
      invalidatesTags: ['Risks'],
    }),
    // The CISO's sign-off on a pending closure (permission APPROVE_RISK_CLOSURE) — the
    // counterpart to closeRisk above for an ever-High risk.
    approveClosure: builder.mutation<ICloseRiskResponse, IApproveClosureRequest>({
      query: ({ riskId }) => ({
        url: `/m3/risks/${riskId}/close/approve`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<Omit<ICloseRiskResponse, 'message'>>) => ({
        ...response.data,
        message: response.message,
      }),
      invalidatesTags: ['Risks'],
    }),
  }),
});

export const {
  useGetRiskListQuery,
  useGetRiskKpisQuery,
  useGetTreatmentStatusReportQuery,
  useGetTreatmentKpisQuery,
  useGetRiskTaxonomyQuery,
  useCreateRiskMutation,
  useDecideFindingRiskMutation,
  useGetIdentifiedRisksQuery,
  useGetIdentifiedRiskQuery,
  useGetRiskAttachmentUploadUrlMutation,
  useRemoveRiskAttachmentMutation,
  useGetRiskByIdQuery,
  useGetRiskScoreQuery,
  useRecordRiskScoreMutation,
  useEditRiskByIdMutation,
  useGetRiskAuditLogQuery,
  useGetRiskTreatmentQuery,
  useCreateTreatmentPlanMutation,
  useGetReviewCycleQuery,
  useGetReviewCycleKpisQuery,
  useSubmitRiskReviewMutation,
  useGetRiskReviewDetailQuery,
  useDecideRiskClosureMutation,
  useApproveTreatmentPlanMutation,
  useSubmitTreatmentActionMutation,
  useSendActionForReviewMutation,
  useGetTreatmentActionQuery,
  useVerifyTreatmentActionMutation,
  useCloseRiskMutation,
  useApproveClosureMutation,
} = riskApis;

export default riskApis;
